import React from 'react';
import { Marker, Tooltip, Popup, Polyline, Circle } from 'react-leaflet';
import L from 'leaflet';
import { medicalNodes, medicalEvacRoutes, dailyMedicalStatus, getMedicalStatusForDayAndNode, timePointToDay, medicalStatusColors, medicalNodeTypeNames } from './medicalSupportData';
import { convoyRouteSegments, alternativeRouteSegment } from './convoyData';
// Create custom icon for medical nodes
const createMedicalNodeIcon = (label: string, type: string, status: string, occupancyPercentage: number) => {
  // Map status to color
  const color = status === 'green' ? '#059669' : status === 'amber' ? '#D97706' : status === 'red' ? '#DC2626' : '#1F2937';
  // Calculate occupancy bar width based on percentage
  const occupancyWidth = Math.max(5, Math.min(100, occupancyPercentage)); // Min 5% for visibility, max 100%
  return L.divIcon({
    html: `
      <div style="display: flex; flex-direction: column; align-items: center;">
        <div style="display: flex; align-items: center; justify-content: center; background-color: white; width: 40px; height: 40px; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.2); border: 3px solid ${color};">
          <div style="font-size: 20px;">🏥</div>
        </div>
        <div style="background-color: ${color}; color: white; font-weight: bold; padding: 2px 6px; border-radius: 4px; font-size: 10px; margin-top: 2px;">
          ${label}
        </div>
        <div style="width: 40px; background-color: #f3f4f6; height: 4px; border-radius: 2px; overflow: hidden; margin-top: 2px;">
          <div style="background-color: ${color}; height: 100%; width: ${occupancyWidth}%;"></div>
        </div>
      </div>
    `,
    className: '',
    iconSize: [40, 70],
    iconAnchor: [20, 40]
  });
};
interface MedicalSupportLayerProps {
  selectedTimePoint: string;
  useAlternativeRoute?: boolean;
}
export const MedicalSupportLayer: React.FC<MedicalSupportLayerProps> = ({
  selectedTimePoint,
  useAlternativeRoute = false
}) => {
  // Convert selected time point to day number
  const currentDay = timePointToDay(selectedTimePoint);
  // Get the route segments based on the selected route
  const routeSegments = convoyRouteSegments.filter(segment => {
    // For day 1, check if we should use the alternative route
    if (segment.day === 1 && useAlternativeRoute) {
      return false;
    }
    return true;
  });
  // Add alternative route if selected
  const allSegments = useAlternativeRoute ? [...routeSegments.filter(segment => segment.day !== 1), alternativeRouteSegment] : routeSegments;
  // Sort segments by day
  const sortedSegments = allSegments.sort((a, b) => a.day - b.day);
  return <>
      {/* Render medical nodes */}
      {medicalNodes.map(node => {
      // Get the current status based on the day
      const currentStatus = getMedicalStatusForDayAndNode(currentDay, node.id);
      // If the current day is before the node's timeline start or after its end,
      // and we're not on day 0 (overview), don't show the node
      if (currentDay !== 0 && (currentDay < node.timeline.start || currentDay > node.timeline.end)) {
        return null;
      }
      // Calculate occupancy percentage based on current status or default to node's value
      const occupancyPercentage = currentStatus ? currentStatus.bedsOccupied / (currentStatus.bedsAvailable + currentStatus.bedsOccupied) * 100 : node.occupancyPercentage;
      // Determine status color based on current status or default to node's value
      const status = currentStatus ? currentStatus.status : node.status;
      return <Marker key={`medical-node-${node.id}`} position={node.coordinates} icon={createMedicalNodeIcon(node.label, node.type, status, occupancyPercentage)} data-id="element-349">
            <Tooltip data-id="element-350">
              <div className="text-sm" data-id="element-351">
                <span className="font-bold" data-id="element-352">{node.label}:</span> {node.name} •
                Beds: {node.bedCapacity.total} • Status:{' '}
                {status === 'green' ? '🟢' : status === 'amber' ? '🟠' : status === 'red' ? '🔴' : '⚫'}{' '}
                {currentStatus ? `${Math.round(100 - occupancyPercentage)}% available` : '100% available'}
              </div>
            </Tooltip>
            <Popup data-id="element-353">
              <div className="min-w-[300px]" data-id="element-354">
                <h3 className="font-bold text-base" data-id="element-355">
                  {node.label} - {node.name}
                </h3>
                <p className="text-sm mt-1" data-id="element-356">
                  <span className="font-medium" data-id="element-357">Type:</span>{' '}
                  {medicalNodeTypeNames[node.type as keyof typeof medicalNodeTypeNames]}
                </p>
                <p className="text-sm" data-id="element-358">
                  <span className="font-medium" data-id="element-359">Distance from Route:</span>{' '}
                  {node.distanceFromRoute}
                </p>
                <div className="mt-3" data-id="element-360">
                  <h4 className="font-medium text-sm border-b pb-1 mb-2" data-id="element-361">
                    Bed Capacity
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-362">
                    <div data-id="element-363">
                      <span className="font-medium" data-id="element-364">Total:</span>{' '}
                      {node.bedCapacity.total}
                    </div>
                    {node.bedCapacity.trauma > 0 && <div data-id="element-365">
                        <span className="font-medium" data-id="element-366">Trauma:</span>{' '}
                        {node.bedCapacity.trauma}
                      </div>}
                    {node.bedCapacity.icu > 0 && <div data-id="element-367">
                        <span className="font-medium" data-id="element-368">ICU:</span>{' '}
                        {node.bedCapacity.icu}
                      </div>}
                    {node.bedCapacity.general > 0 && <div data-id="element-369">
                        <span className="font-medium" data-id="element-370">General:</span>{' '}
                        {node.bedCapacity.general}
                      </div>}
                    {node.bedCapacity.holding > 0 && <div data-id="element-371">
                        <span className="font-medium" data-id="element-372">Holding:</span>{' '}
                        {node.bedCapacity.holding}
                      </div>}
                  </div>
                </div>
                <div className="mt-3" data-id="element-373">
                  <h4 className="font-medium text-sm border-b pb-1 mb-2" data-id="element-374">
                    Capabilities
                  </h4>
                  <ul className="list-disc pl-5 text-sm" data-id="element-375">
                    {node.capabilities.map((capability, index) => <li key={index} data-id="element-376">{capability}</li>)}
                  </ul>
                </div>
                <div className="mt-3" data-id="element-377">
                  <h4 className="font-medium text-sm border-b pb-1 mb-2" data-id="element-378">
                    Staffing
                  </h4>
                  <ul className="list-disc pl-5 text-sm" data-id="element-379">
                    {node.staff.map((staffMember, index) => <li key={index} data-id="element-380">{staffMember}</li>)}
                  </ul>
                </div>
                {/* Current Status Section */}
                {currentStatus && <div className="mt-3" data-id="element-381">
                    <h4 className="font-medium text-sm border-b pb-1 mb-2" data-id="element-382">
                      Current Status (Day {currentDay})
                    </h4>
                    <div className="text-sm space-y-1" data-id="element-383">
                      <div data-id="element-384">
                        <span className="font-medium" data-id="element-385">Status:</span>{' '}
                        <span className={`${status === 'green' ? 'text-green-600' : status === 'amber' ? 'text-amber-600' : status === 'red' ? 'text-red-600' : 'text-gray-800'}`} data-id="element-386">
                          {status === 'green' && '🟢 Green'}
                          {status === 'amber' && '🟠 Amber'}
                          {status === 'red' && '🔴 Red'}
                          {status === 'black' && '⚫ Black'}
                        </span>
                      </div>
                      <div data-id="element-387">
                        <span className="font-medium" data-id="element-388">Beds Available:</span>{' '}
                        {currentStatus.bedsAvailable} of{' '}
                        {currentStatus.bedsAvailable + currentStatus.bedsOccupied}
                      </div>
                      <div data-id="element-389">
                        <span className="font-medium" data-id="element-390">Occupancy:</span>{' '}
                        {Math.round(occupancyPercentage)}%
                      </div>
                      <div data-id="element-391">
                        <span className="font-medium" data-id="element-392">Notes:</span>{' '}
                        {currentStatus.notes}
                      </div>
                    </div>
                  </div>}
                {/* Support Timeline */}
                <div className="mt-3" data-id="element-393">
                  <h4 className="font-medium text-sm border-b pb-1 mb-2" data-id="element-394">
                    Support Timeline
                  </h4>
                  <p className="text-sm" data-id="element-395">
                    <span className="font-medium" data-id="element-396">Active:</span> Day{' '}
                    {node.timeline.start} to Day {node.timeline.end}
                  </p>
                  <p className="text-sm" data-id="element-397">
                    <span className="font-medium" data-id="element-398">Support Role:</span>{' '}
                    {node.supportRole}
                  </p>
                </div>
                {/* Evacuation Route */}
                {node.evacuationRoute && <div className="mt-3" data-id="element-399">
                    <h4 className="font-medium text-sm border-b pb-1 mb-2" data-id="element-400">
                      Evacuation
                    </h4>
                    <p className="text-sm" data-id="element-401">
                      <span className="font-medium" data-id="element-402">EVAC Route:</span>{' '}
                      {node.evacuationRoute}
                    </p>
                  </div>}
              </div>
            </Popup>
          </Marker>;
    })}
      {/* Render medical evacuation routes */}
      {medicalEvacRoutes.map(route => <Polyline key={`medical-evac-route-${route.id}`} positions={route.coordinates} pathOptions={{
      color: route.transportType === 'air' ? '#3B82F6' : '#DC2626',
      weight: 2,
      opacity: 0.7,
      dashArray: route.transportType === 'air' ? '1, 10' : '10, 5'
    }} data-id="element-403">
          <Tooltip data-id="element-404">
            {route.name} ({route.transportType === 'air' ? 'Air' : 'Ground'}{' '}
            EVAC)
          </Tooltip>
          <Popup data-id="element-405">
            <div data-id="element-406">
              <h3 className="font-bold" data-id="element-407">{route.name}</h3>
              <p className="text-sm mt-1" data-id="element-408">{route.description}</p>
              <div className="mt-2 space-y-1 text-sm" data-id="element-409">
                <div data-id="element-410">
                  <span className="font-medium" data-id="element-411">Transport Type:</span>{' '}
                  {route.transportType === 'air' ? 'Air CASEVAC' : route.transportType === 'ground' ? 'Ground MEDEVAC' : 'Combined Air/Ground'}
                </div>
                <div data-id="element-412">
                  <span className="font-medium" data-id="element-413">Estimated Time:</span>{' '}
                  {route.estimatedTimeMinutes} minutes
                </div>
              </div>
            </div>
          </Popup>
        </Polyline>)}
      {/* Render coverage circles for medical nodes */}
      {medicalNodes.map(node => {
      // Only show coverage for nodes that are active in the current timeline
      if (currentDay !== 0 && (currentDay < node.timeline.start || currentDay > node.timeline.end)) {
        return null;
      }
      // Different coverage radius based on node type
      const radius = node.type === 'role3' ? 75000 // 75km for Role III
      : node.type === 'role2plus' ? 50000 // 50km for Role II+
      : 30000; // 30km for others
      return <Circle key={`medical-coverage-${node.id}`} center={node.coordinates} radius={radius} pathOptions={{
        color: '#DC2626',
        fillColor: '#DC2626',
        fillOpacity: 0.05,
        weight: 1,
        dashArray: '5, 5'
      }} data-id="element-414">
            <Tooltip data-id="element-415">
              {node.label} {node.name} Coverage Area
            </Tooltip>
          </Circle>;
    })}
      {/* Render a summary table when clicking on the route */}
      {sortedSegments.length > 0 && <Polyline positions={sortedSegments.flatMap(segment => segment.coordinates)} pathOptions={{
      color: 'transparent',
      weight: 20,
      opacity: 0.01
    }} data-id="element-416">
          <Popup data-id="element-417">
            <div className="min-w-[300px]" data-id="element-418">
              <h3 className="font-bold text-base mb-2" data-id="element-419">
                Medical Support Summary
              </h3>
              <table className="w-full text-sm border-collapse" data-id="element-420">
                <thead data-id="element-421">
                  <tr className="bg-gray-100" data-id="element-422">
                    <th className="border px-2 py-1 text-left" data-id="element-423">Node</th>
                    <th className="border px-2 py-1 text-left" data-id="element-424">Type</th>
                    <th className="border px-2 py-1 text-right" data-id="element-425">Beds</th>
                    <th className="border px-2 py-1 text-left" data-id="element-426">Timeline</th>
                  </tr>
                </thead>
                <tbody data-id="element-427">
                  {medicalNodes.map(node => <tr key={`summary-node-${node.id}`} data-id="element-428">
                      <td className="border px-2 py-1" data-id="element-429">
                        {node.label} - {node.name.split(' ')[0]}
                      </td>
                      <td className="border px-2 py-1" data-id="element-430">
                        {node.type === 'role3' ? 'Role III' : node.type === 'role2plus' ? 'Role II+' : node.type === 'role2' ? 'Role II' : 'Role I'}
                      </td>
                      <td className="border px-2 py-1 text-right" data-id="element-431">
                        {node.bedCapacity.total}
                      </td>
                      <td className="border px-2 py-1" data-id="element-432">
                        Day {node.timeline.start}–{node.timeline.end}
                      </td>
                    </tr>)}
                </tbody>
              </table>
              <h4 className="font-medium text-sm mt-4 mb-2" data-id="element-433">
                Bed Status by Day
              </h4>
              <table className="w-full text-sm border-collapse" data-id="element-434">
                <thead data-id="element-435">
                  <tr className="bg-gray-100" data-id="element-436">
                    <th className="border px-2 py-1 text-left" data-id="element-437">Day</th>
                    <th className="border px-2 py-1 text-left" data-id="element-438">Node</th>
                    <th className="border px-2 py-1 text-right" data-id="element-439">Available</th>
                    <th className="border px-2 py-1 text-right" data-id="element-440">Occupied</th>
                    <th className="border px-2 py-1 text-left" data-id="element-441">Status</th>
                  </tr>
                </thead>
                <tbody data-id="element-442">
                  {dailyMedicalStatus.map((status, index) => {
                const node = medicalNodes.find(n => n.id === status.nodeId);
                return <tr key={`status-day-${status.day}-${status.nodeId}`} data-id="element-443">
                        <td className="border px-2 py-1" data-id="element-444">{status.day}</td>
                        <td className="border px-2 py-1" data-id="element-445">{node?.label}</td>
                        <td className="border px-2 py-1 text-right" data-id="element-446">
                          {status.bedsAvailable}
                        </td>
                        <td className="border px-2 py-1 text-right" data-id="element-447">
                          {status.bedsOccupied}
                        </td>
                        <td className="border px-2 py-1" data-id="element-448">
                          <span className={`inline-block w-3 h-3 rounded-full mr-1 ${status.status === 'green' ? 'bg-green-600' : status.status === 'amber' ? 'bg-amber-600' : status.status === 'red' ? 'bg-red-600' : 'bg-gray-800'}`} data-id="element-449"></span>
                          {status.status.charAt(0).toUpperCase() + status.status.slice(1)}
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
              <div className="mt-3 text-xs text-gray-500" data-id="element-450">
                <div className="font-medium mb-1" data-id="element-451">Legend:</div>
                <div className="flex items-center" data-id="element-452">
                  <span className="inline-block w-3 h-3 rounded-full mr-1 bg-green-600" data-id="element-453"></span>
                  <span data-id="element-454">Green: Good capacity</span>
                </div>
                <div className="flex items-center" data-id="element-455">
                  <span className="inline-block w-3 h-3 rounded-full mr-1 bg-amber-600" data-id="element-456"></span>
                  <span data-id="element-457">Amber: Limited capacity</span>
                </div>
                <div className="flex items-center" data-id="element-458">
                  <span className="inline-block w-3 h-3 rounded-full mr-1 bg-red-600" data-id="element-459"></span>
                  <span data-id="element-460">Red: Critical capacity</span>
                </div>
                <div className="flex items-center" data-id="element-461">
                  <span className="inline-block w-3 h-3 rounded-full mr-1 bg-gray-800" data-id="element-462"></span>
                  <span data-id="element-463">Black: No capacity</span>
                </div>
              </div>
            </div>
          </Popup>
        </Polyline>}
    </>;
};